import { useCallback, useId, useRef, useState } from 'react';
import Calendar from './Calendar';
import PickerField from './PickerField';
import Popover from './Popover';
import { formatDateBy, toDate } from '../../utils/dates';

const DateRangePicker = ({
    value,
    onChange,
    placeholder = 'Select dates',
    format = 'dd MMM yyyy',
    minDate,
    maxDate,
    excludeIntervals = [],
    monthsShown = 2,
    clearable = false,
    disabled = false,
    invalid = false,
    size = 'md',
    className,
    ariaLabel,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const baseId = useId();
    const titleId = `${baseId}-title`;
    const popupId = `${baseId}-popup`;

    const from = toDate(value?.from);
    const to = toDate(value?.to);

    const close = useCallback((reason) => {
        setOpen(false);
        if (reason === 'escape' || reason === 'select') {
            anchorRef.current?.querySelector('.dt-field__input')?.focus();
        }
    }, []);

    const label = from && to
        ? `${formatDateBy(from, format)} — ${formatDateBy(to, format)}`
        : from
            ? `${formatDateBy(from, format)} — …`
            : '';

    const handleSelect = (range, complete) => {
        onChange(range);
        if (complete) close('select');
    };

    return (
        <>
            <PickerField
                ref={anchorRef}
                icon="fa-calendar-days"
                value={label}
                placeholder={placeholder}
                open={open}
                popupId={popupId}
                disabled={disabled}
                invalid={invalid}
                clearable={clearable}
                size={size}
                className={className}
                ariaLabel={ariaLabel || placeholder}
                onOpen={() => setOpen(true)}
                onClear={() => onChange({ from: null, to: null })}
            />

            <Popover
                anchorRef={anchorRef}
                id={popupId}
                open={open}
                onClose={close}
                labelledBy={titleId}
                className="dt-popover--calendar"
            >
                <Calendar
                    mode="range"
                    value={{ from, to }}
                    onSelect={handleSelect}
                    minDate={minDate}
                    maxDate={maxDate}
                    excludeIntervals={excludeIntervals}
                    monthsShown={monthsShown}
                    titleId={titleId}
                />

                <div className="dt-panel__footer">
                    <span className="dt-panel__hint">
                        {from && !to ? 'Pick the end date' : 'Pick the start date'}
                    </span>
                    {clearable && (
                        <button
                            type="button"
                            className="dt-panel__action"
                            onClick={() => {
                                onChange({ from: null, to: null });
                                close('select');
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </Popover>
        </>
    );
};

export default DateRangePicker;
